import {
    DataTable,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Layer,
    Tooltip
} from '@carbon/react';
import {useContext, useEffect, useState, useMemo} from 'react';
import {observer} from 'mobx-react-lite';
import {NetworkContext} from '../lib/context';
import ComplianceIndicator from './compliance-indicator';
import {Information} from '@carbon/icons-react';
import './compliance-score.scss';
import Spinner from '../../../components/spinner';
import ErrorDisplay from './error-display';
import {parseFilterValues} from './no-filters/utils/parse-filter-values';

type Props = {
    compactView?: boolean,
    specialtyLoading?: boolean,
    specialtyError?: string | null
}

function ComplianceScore(props: Props) {
    const {compactView, specialtyLoading, specialtyError} = props;
    const networkOptimizationStore = useContext(NetworkContext);
    const loading = useMemo(() => specialtyLoading ?? false, [specialtyLoading]);
    const [_error, setError] = useState<string | null>(null);
    const error = useMemo(() => specialtyError || _error, [specialtyError, _error]);
    const {complianceScores} = networkOptimizationStore;

    // Clear error when loading starts
    useEffect(() => {
        if (loading) {
            setError(null);
        }
    }, [loading]);

    const specialtyCodes = useMemo(() =>
        parseFilterValues(networkOptimizationStore.specialtyFilter),
    [networkOptimizationStore.specialtyFilter]
    );

    const singleSpecialtyCode = useMemo(() =>
        specialtyCodes.length === 1 ? specialtyCodes[0] : null,
    [specialtyCodes]
    );

    const singleSpecialtyName = useMemo(() =>
        singleSpecialtyCode
            ? complianceScores.find((s: any) => s.specialtyCode === singleSpecialtyCode)?.name
            : null,
    [singleSpecialtyCode, complianceScores]
    );

    const {selectedScore, headers} = useMemo(() => {
        try {
            const selectedScore = singleSpecialtyCode
                ? complianceScores?.find((score: { specialtyCode: string }) => score.specialtyCode === singleSpecialtyCode)
                : null;

            const finalScore = selectedScore || networkOptimizationStore.calculateGeneralComplianceScores();
            const headers = Object.keys(finalScore.scores)
                .map((key) => finalScore.scores[key].name)
                .filter((name) => name !== 'Min Required' || !singleSpecialtyCode);

            return {selectedScore: finalScore, headers};
        } catch (err) {
            setError('Error generating estimated compliance scores.');
            return {selectedScore: null, headers: []};
        }
    }, [complianceScores, singleSpecialtyCode, networkOptimizationStore]);

    const getTooltipLabel = (header: string): string => {
        const isSpecialtySelected = specialtyCodes.length === 1;
    
        const tooltipLabels: { [key: string]: string } = {
            'Time': 'The percentage of the provider roster for the selected specialty that meets the driving time standard for the patient population.',
            'Distance': 'The percentage of the provider roster for the selected specialty that meets the driving distance standard for the patient population.',
            '# of Distinct Providers': 'The number of providers in the selected specialty that are included in the current roster.',
            '# of Provider Locations': 'The number of provider locations in the selected specialty that are included in the current roster.',
            '# of Beds': 'The number of beds in the selected facility.',
            'Estimated Total Compliance': isSpecialtySelected
                ? '✓ indicates the current roster is compliant with the CMS adequacy guidelines. ✗ indicates the plan is not in compliance with the CMS adequacy guidelines. Add providers from the table in the Explore Additional Providers tab to meet compliance standards.'
                : 'The number of specialties in your roster that meet the time and distance standard divided by the number of specialties that are included in the CMS adequacy standard.'
        };
    
        return tooltipLabels[header] || 'No tooltip available'; 
    };

    function renderDefaultView() {
        const hasSpecialtySelected = specialtyCodes.length === 1;

        const additionalHeaders = hasSpecialtySelected ? [] : ['Invisible 1', 'Invisible 2'];

        const additionalScores = hasSpecialtySelected ? [] : [
            {name: '', value: '', compliant: null},
            {name: '', value: '', compliant: null}
        ];

        const allHeaders = [...additionalHeaders, ...headers];
        const allScores = [...additionalScores, ...selectedScore.scores.slice(0, 4)];

        return (
            <Layer className='h-full'>
                <div className="compliance-score bg-white h-full flex flex-col relative overflow-visible">
                    <h1 className="text-lg font-semibold py-4 pl-4 m-0" data-testid="compliance-score-title">
                        {hasSpecialtySelected
                            ? `Estimated Total Compliance - ${singleSpecialtyName}`
                            : 'Estimated Total Compliance'}
                    </h1>
    
                    <TableContainer>
                        <DataTable
                            rows={[selectedScore]}
                            headers={allHeaders.map((header) => ({
                                key: header,
                                header: header
                            }))}
                        >
                            {({headers}) => (
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            {headers.map((header) => {
                                                const headerLabel = header.header as string;
                                                return (
                                                    <TableHeader
                                                        key={header.key}
                                                        className={`bg-gray-100 ${headerLabel.startsWith('Invisible') ? 'w-1/12' : 'w-3/12'}`}  
                                                    >
                                                        {headerLabel.startsWith('Invisible') ? null : (
                                                            <div className="flex items-center justify-end gap-1">
                                                                <span>{headerLabel}</span>
                                                                <Tooltip label={getTooltipLabel(headerLabel)} align="top" autoAlign>
                                                                    <button type="button" aria-label="Information">
                                                                        <Information size={14} className="hover:text-blue-500" />
                                                                    </button>
                                                                </Tooltip>
                                                            </div>
                                                        )}
                                                    </TableHeader>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            {allScores.map((score: { name: string, value: number, compliant: boolean, cssClasses?: string }, index: number) => (
                                                <TableCell
                                                    key={index}
                                                    className="w-3/12"
                                                >
                                                    <div className="flex justify-end items-center space-x-3">
                                                        <span>{score.value}</span>
                                                        {score.compliant !== null && <ComplianceIndicator passed={score.compliant} />}
                                                    </div>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            )}
                        </DataTable>
                    </TableContainer>
    
                </div>
            </Layer>
        );
    }
    
    
    
    function renderCompactView() {
        const compactScores = selectedScore.scores.slice(0, 4);
        const hasSpecialtySelected = specialtyCodes.length === 1;

        return (
            <div className="bg-white">
                <h1 className="text-lg font-semibold py-4 pl-4 m-0">
                    {hasSpecialtySelected
                        ? `Estimated Total Compliance - ${singleSpecialtyName}`
                        : 'Estimated Total Compliance'}
                </h1>
                <ul className="flex flex-col">
                    {compactScores.map((score: { name: string, value: number, compliant: boolean, cssClasses?: string }, index: number) => (
                        <li key={index} className={`flex justify-between items-center border-b border-gray-300 p-2 ${score.cssClasses}`}>
                            <div className="flex items-center">
                                <span>{score.name}</span>
                                <Tooltip label={getTooltipLabel(score.name)} align="top" autoAlign>
                                    <button className="sb-tooltip-trigger ml-2" type="button" aria-label="Information">
                                        <Information className="hover:text-blue-500" />
                                    </button>
                                </Tooltip>
                            </div>
                            <div className="w-1/4 flex justify-end space-x-3 items-center">
                                <span>{score.value}</span>
                                {score.compliant !== null && <ComplianceIndicator passed={score.compliant} />}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    if (loading || !complianceScores || Object.keys(complianceScores).length === 0)
        return networkOptimizationStore.readyToLoadData() ? <div className="flex items-center space-x-3"> <Spinner /> <span className="block">Loading estimated compliance scores...</span></div> : null;

    if (error) {
        return <ErrorDisplay error={error} title='Unable to Load Compliance Scores' />;
    }

    return compactView ? renderCompactView() : renderDefaultView();
}

export default observer(ComplianceScore);
